import { useField } from 'formik';
import { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { Button } from "react-bootstrap";
import Icon from "../../controls/Icon";
import GenericTextBox from '../../controls/GenericTextBox';
import { useTranslation } from 'react-i18next';
import _ from "lodash";
import ListStatus from './controls/ListStatus';
import { useDrag, useDrop } from 'react-dnd';
import GenericDropdown from '../../controls/GenericDropdown';
import { useFormsConfig } from './Context';

const DataListOptionControl = ({
    name,
    onChange
}) => {
    const [translate] = useTranslation();
    const [field, meta, { setValue, setTouched, }] = useField(name);

    const [options, setOptions] = useState([]);

    useEffect(() => {
        if (name && field.value) {
            setOptions(_.cloneDeep(field.value).map((option, i) => ({ ...option, order: i + 1 })));
        }

    }, [name, field.value, setOptions]);

    const fieldTouched = useCallback((index, fieldName) => {
        return options?.[index]?.[`_touched_${fieldName}`] ? true : false;
    }, [options]);

    const error = useMemo(() => {
        if (meta.touched && meta.error && meta.value && meta.value.length > 0) {
            let messages = {};
            meta.error.forEach((error, index) => {
                if (!error) { return; }
                Object.keys(error).forEach(fieldName => {
                    if (fieldTouched(index, fieldName)) {
                        messages[fieldName] = error[fieldName];
                    }
                })
            });
            return <div className="col-12 col-lg">
                <div className='text-danger small'>{Object.values(messages).join(", ")}</div>
            </div>;
        }

        return null;
    }, [meta, fieldTouched]);

    const getErrorBorder = useCallback((index, fieldName) => {
        if (!meta.error) {
            return '';
        }

        if (meta?.error.length <= 0 || !meta?.error[index] || !meta?.error[index][fieldName]) {
            return '';
        }

        return 'border-danger';
    }, [meta]);

    const blurOptionField = useCallback((index, fieldName) => {
        options[index][`_touched_${fieldName}`] = true;
        setOptions(options);
        setTouched(true);
    }, [options, setOptions, setTouched]);

    const handleFieldChange = useCallback((newValue) => {
        setTouched(true);
        setValue(newValue);
        if (onChange) {
            onChange(newValue);
        }
    }, [onChange, setValue, setTouched]);

    const updateOptionField = useCallback((index, fieldName, value) => {
        options[index][fieldName] = value;
        options[index][`_touched_${fieldName}`] = true;
        setOptions(options);
        handleFieldChange(options);
    }, [options, setOptions, handleFieldChange]);

    const addItem = useCallback(() => {
        options.push({ value: "", displayValue: "" });
        setOptions(options);
        handleFieldChange(options);
    }, [options, setOptions, handleFieldChange]);

    const removeItem = useCallback((index) => {
        options.splice(index, 1);
        setOptions(options);
        handleFieldChange(options);
    }, [options, setOptions, handleFieldChange]);

    const onReorder = useCallback((oldIndex, newIndex) => {
        if (oldIndex < newIndex) {
            for (let i = oldIndex; i < newIndex; i += 1) {
                const aux = { ...options[i] };
                options[i] = { ...options[i + 1] };
                options[i + 1] = { ...aux };
            }
        }
        else {
            for (let i = oldIndex; i > newIndex; i -= 1) {
                const aux = { ...options[i] };
                options[i] = { ...options[i - 1] };
                options[i - 1] = { ...aux };

            }
        }
        setOptions([...options]);
    }, [options]);

    const onReorderDrop = useCallback(async () => {
        handleFieldChange(options);
    }, [handleFieldChange, options,]);

    const renderOptions = useMemo(() => {
        return <div >
            {options.length > 0 ? options.map((option, index) => <DataListOption
                option={option}
                onReorder={onReorder}
                onReorderDrop={onReorderDrop}
                updateOptionField={updateOptionField}
                removeItem={removeItem}
                blurOptionField={blurOptionField}
                getErrorBorder={getErrorBorder}
                key={option.order}
                index={index} />)
                :
                <ListStatus loading={false} error={false} empty={true} emptyText={translate("OTM_NO_ITEMS")} ></ListStatus>}
        </div>
    }, [options, translate, onReorder, onReorderDrop, removeItem, updateOptionField, getErrorBorder, blurOptionField]);

    return <div className="col-12 mt-3">
        <label className='form-label h6'>{translate('OTM_OPTIONS')}</label>
        {renderOptions}
        <div className="row gx-2 align-items-start justify-content-end">
            {error}
            <div className="col-auto mt-2">
                <Button
                    size="sm"
                    variant="primary"
                    onClick={() => { addItem() }}
                >{translate("OTM_ADD_ITEM")}</Button>
            </div>
        </div>
    </div>
}

const DataListOption = ({
    option,
    onReorder,
    onReorderDrop,
    removeItem,
    updateOptionField,
    blurOptionField,
    getErrorBorder,
    index
}) => {
    const ref = useRef(null);
    const [translate] = useTranslation();
    const { dataListsColorOptions } = useFormsConfig();

    const [{ isDragging }, drag] = useDrag({
        type: 'dataList-option',
        item: () => ({
            index
        }),
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    }, []);

    const [{ canDrop }, drop] = useDrop({
        accept: 'dataList-option',
        hover: (item, monitor) => {
            const dragIndex = item.index;
            const hoverIndex = index;

            if (ref.current && dragIndex !== hoverIndex) {
                const hoverBoundingRect = ref.current.getBoundingClientRect();
                const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
                const clientOffset = monitor.getClientOffset();
                const hoverClientY = clientOffset.y - hoverBoundingRect.top;

                const isDraggingDown = dragIndex < hoverIndex && hoverClientY > hoverMiddleY;
                const isDraggingUp = dragIndex > hoverIndex && hoverClientY < hoverMiddleY;

                if (isDraggingDown || isDraggingUp) {
                    onReorder(dragIndex, hoverIndex);
                    item.index = hoverIndex;
                }
            }
        },
        collect: (monitor) => ({
            canDrop: monitor.canDrop()
        }),
        drop: async () => {
            await onReorderDrop();
        }
    }, [onReorder]);

    drag(drop(ref));

    return <div className={`${index > 0 ? "mt-2 pt-2 pt-lg-0 border-top border-dark border-2 border-lg-0" : ""}`}>
        <div ref={ref} className={`row align-items-center justify-content-end mt-n1 ${isDragging ? 'forms-item-dragging' : ''}`}>
            {canDrop && < div className="col-auto p-0 order-first">
                {option.order === index + 1 ? null : <span className="text-decoration-line-through">{option.order}</span>}
                {<span className={`ms-1 ${option.order === index + 1 ? '' : 'text-tertiary'}`}>{index + 1}</span>}
            </div>}
            <div className="col-auto p-0">
                <Icon icon={'DragIndicatorOutlined'} className="text-tertiary" />
            </div>
            <div className="col col-lg mt-1">
                <GenericTextBox
                    type="text"
                    placeholder={translate("OTM_DISPLAY_VALUE")}
                    defaultValue={option.displayValue}
                    onBlur={() => { blurOptionField(index, "displayValue") }}
                    onChange={(event) => { updateOptionField(index, "displayValue", event.target.value) }}
                    css={{ input: `form-control-sm ${getErrorBorder(index, "displayValue")}` }}
                />
            </div>
            <div className="col-12 d-lg-none"></div>
            <div className="col mt-1">
                <GenericTextBox
                    type="text"
                    placeholder={translate("OTM_DATA_VALUE")}
                    defaultValue={option.value}
                    onBlur={() => { blurOptionField(index, "value") }}
                    onChange={(event) => { updateOptionField(index, "value", event.target.value) }}
                    css={{ input: `form-control-sm ${getErrorBorder(index, "value")}` }}
                />
            </div>
            <div className="col-2 mt-1">
                <GenericDropdown
                    size="sm"
                    grow={true}
                    defaultValue={option.variant}
                    valueKey="value"
                    displayValueKey="displayValue"
                    options={dataListsColorOptions}
                    onSelect={(value) => { updateOptionField(index, "variant", value) }}
                    css={{ input: `form-control-sm ${getErrorBorder(index, "variant")}` }}
                />
            </div>
            <div className="col-auto mt-1">
                <Button
                    variant="outline-danger"
                    className="btn-sm d-block p-0 border-0"
                    onClick={() => { removeItem(index) }}
                    title={translate("OTM_DELETE")}
                ><Icon icon="Delete" className="d-block" /></Button>
            </div>
        </div>
    </div >
}


export default DataListOptionControl;